import { useEffect, useMemo, useState } from 'react';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

export const useRecordTable = <T extends object>({
  columns,
  data,
  pageSize,
}: {
  columns: Column<T>[];
  data: T[];
  pageSize: number;
}) => {
  const [filters, setFilters] = useState<Record<string, string>>({});

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      Object.entries(filters).every(([key, value]) => {
        const field = item[key];

        if (field === undefined || field === null) {
          // 🚨 Soporte especial para columnas personalizadas como Producto/Servicio
          if (key === 'productName' && 'serviceDescription' in item) {
            const product = item['productName']?.toString().toLowerCase() || '';
            const service = item['serviceDescription']?.toString().toLowerCase() || '';
            return (
              product.includes(value.toLowerCase()) ||
              service.includes(value.toLowerCase())
            );
          }
          return false;
        }

        return field.toString().toLowerCase().includes(value.toLowerCase());
      })
    );
  }, [data, filters]);



  useEffect(() => {
    // Bail out via the functional-update form when filters are already
    // empty: returning the same reference makes React skip the re-render,
    // so an unstable `data` reference (recreated every parent render)
    // can't force this effect into re-firing on every single render.
    setFilters((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [data]);

  const memoizedData = useMemo(() => filteredData, [filteredData]);

  const tableInstance = useTable(
    {
      columns: columns as readonly Column<any>[],
      data: memoizedData,
      initialState: { pageIndex: 0, pageSize },
    },
    useSortBy,
    usePagination
  );

  const { gotoPage, pageCount, setPageSize } = tableInstance;

  return {
    ...tableInstance,
    filters,
    setFilters,
    memoizedData,
    gotoPage,
    pageCount,
    setPageSize,
  };
};
